import { useMemo, useState } from 'react';
import { Bot, PenLine, Plus, RefreshCw } from 'lucide-react';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Carte,
  Chargement,
  EnteteEcran,
  MessageErreur,
  Pastille,
} from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { useToast } from '../components/ui/useToast';
import { ModaleEcheance } from '../components/veille/ModaleEcheance';
import { ModaleDetection } from '../components/veille/ModaleDetection';
import { ModaleSaisieDetection } from '../components/veille/ModaleSaisieDetection';
import { ModaleSources } from '../components/veille/ModaleSources';
import { instantLisible, jourLisible } from '../components/veille/format';
import { usePerimetre } from '../site/usePerimetre';
import { ErreurApi } from '../lib/api';
import {
  useLancerAnalyse,
  useVeille,
  type LigneDetection,
  type LigneEcheance,
} from '../lib/useVeille';

/** Détections montrées avant « Tout afficher ». */
const DETECTIONS_REPLIEES = 8;

const PASTILLE_NIVEAU: Record<LigneDetection['ton'], string> = {
  danger: 'bg-red-500',
  warning: 'bg-amber-500',
  info: 'bg-codialis-sky',
};

function sousTitreEcheance(echeance: LigneEcheance): string {
  const morceaux = [
    `${echeance.nature} ${echeance.dateEcheance ? `le ${jourLisible(echeance.dateEcheance)}` : '· date à relever'}`,
    echeance.siteCode ? `Antenne ${echeance.siteCode}` : 'Tout l’organisme',
  ];
  if (echeance.formations.length > 0) morceaux.push(echeance.formations.map((formation) => formation.code).join(', '));
  return morceaux.join(' · ');
}

/**
 * Écran « Veille réglementaire & pédagogique » (CDC §6.11).
 *
 * Le robot lit chaque matin les fiches RNCP du catalogue à France Compétences
 * et la liste publique des organismes de formation ; il tient les échéances de
 * fin d'enregistrement et relève les évolutions. Les échéances sans source
 * ouverte (agrément, certification qualité, habilitation) et les évolutions
 * qu'aucun flux ne publie se saisissent ici. L'alerte part quatre mois avant
 * chaque échéance, et à chaque évolution relevée. L'API dit l'état de chaque
 * ligne et ce qui empêche un geste ; l'écran ne recalcule rien.
 */
export function PedVeillePage(): JSX.Element {
  const { site } = usePerimetre();
  const { data, isLoading, error } = useVeille(site);
  const analyse = useLancerAnalyse();
  const notifier = useToast();

  const [echeanceOuverte, setEcheanceOuverte] = useState<LigneEcheance | null>(null);
  const [nouvelleEcheance, setNouvelleEcheance] = useState(false);
  const [detectionOuverte, setDetectionOuverte] = useState<LigneDetection | null>(null);
  const [saisieDetection, setSaisieDetection] = useState(false);
  const [sourcesOuvertes, setSourcesOuvertes] = useState(false);
  const [toutesDetections, setToutesDetections] = useState(false);
  const [toutAfficher, setToutAfficher] = useState(false);

  const preavis = data?.preavisMois ?? 4;
  const aAnalyser = data?.compteurs.aAnalyser ?? 0;
  const detections = useMemo(() => {
    const lignes = data?.detections ?? [];
    return toutesDetections || aAnalyser === 0 ? lignes : lignes.filter((ligne) => ligne.statut === 'A_ANALYSER');
  }, [data?.detections, toutesDetections, aAnalyser]);
  const detectionsVisibles = toutAfficher ? detections : detections.slice(0, DETECTIONS_REPLIEES);

  const dernierPassage = useMemo(() => {
    const passages = (data?.sources ?? [])
      .map((source) => source.dernierPassage)
      .filter((passage): passage is NonNullable<typeof passage> => passage !== null)
      .sort((a, b) => b.debuteLe.localeCompare(a.debuteLe));
    return passages[0] ?? null;
  }, [data?.sources]);
  const sourcesEnEchec = (data?.sources ?? []).filter((source) => source.dernierPassage?.succes === false);
  const enCours = analyse.isPending || data?.analyseEnCours === true;

  function lancer(): void {
    analyse.mutate(undefined, { onSuccess: (resultat) => notifier(resultat.message) });
  }

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Veille réglementaire & pédagogique"
        sousTitre="Pôle pédagogique · surveillance automatisée RNCP, agréments, évolutions numériques"
        actions={
          <>
            <BoutonSecondaire onClick={() => setSourcesOuvertes(true)}>Sources surveillées</BoutonSecondaire>
            <BoutonPrincipal onClick={lancer} disabled={enCours || !data}>
              <RefreshCw
                className={`mr-2 h-4 w-4 ${enCours ? 'animate-spin motion-reduce:animate-none' : ''}`}
                aria-hidden="true"
              />
              {enCours ? 'Analyse en cours…' : 'Lancer une analyse'}
            </BoutonPrincipal>
          </>
        }
      />

      <BarreSites />

      <div className="grid grid-cols-1 items-center gap-5 rounded-2xl bg-gradient-to-br from-codialis-navy to-codialis-blue p-5 text-white sm:p-6 lg:grid-cols-4">
        <div className="min-w-0 lg:col-span-3">
          <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-codialis-mint">
            Robot de veille · exécution quotidienne
          </div>
          <h2 className="font-display mb-1 text-xl font-bold">
            Une alerte {preavis} mois avant chaque échéance d’agrément ou de certification
          </h2>
          <p className="text-sm text-white/70">
            Le robot surveille chaque matin les fiches RNCP des formations dispensées (France Compétences) et
            la déclaration d’activité et la certification Qualiopi de l’organisme (liste publique de la DGEFP).
            Toute évolution déclenche une alerte au pédagogique et à la direction.
          </p>
          <p className="mt-2 text-xs text-white/60">
            {dernierPassage
              ? `Dernière analyse le ${instantLisible(dernierPassage.debuteLe)}${
                  dernierPassage.lancePar ? ` · lancée par ${dernierPassage.lancePar}` : ' · passage quotidien'
                }`
              : 'Aucune analyse encore : la première aura lieu demain à 6 h, ou maintenant.'}
          </p>
        </div>
        <div className="text-center">
          <div className="font-display text-4xl font-extrabold text-codialis-mint">{preavis} mois</div>
          <div className="mt-1 text-xs text-white/60">avant échéance</div>
        </div>
      </div>

      {error instanceof ErreurApi && <MessageErreur message={error.message} />}
      {analyse.error instanceof ErreurApi && <MessageErreur message={analyse.error.message} />}
      {sourcesEnEchec.length > 0 && (
        <MessageErreur
          message={`Dernière lecture en échec : ${sourcesEnEchec
            .map((source) => `${source.libelle} — ${source.dernierPassage?.message ?? 'erreur inconnue'}`)
            .join(' ; ')}`}
        />
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="min-w-0">
          <Carte
            titre="Échéances surveillées"
            actions={
              <button
                type="button"
                onClick={() => setNouvelleEcheance(true)}
                className="inline-flex min-h-[36px] items-center gap-1 rounded-lg px-2 text-xs font-bold text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[44px]"
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                Suivre une échéance
              </button>
            }
          >
            {isLoading && <Chargement />}
            {!isLoading && data && data.echeances.length === 0 && (
              <p className="px-4 py-8 text-sm text-codialis-gray sm:px-6">
                Aucune échéance suivie sur ce périmètre. Les fiches RNCP des formations s’y ajoutent au
                premier passage du robot ; les agréments, la certification qualité et les habilitations se
                saisissent avec « Suivre une échéance ».
              </p>
            )}
            {data?.echeances.map((echeance) => (
              <button
                key={echeance.id}
                type="button"
                onClick={() => setEcheanceOuverte(echeance)}
                className="flex w-full items-center gap-3 px-4 py-4 text-left transition hover:bg-gray-50 sm:gap-4 sm:px-6"
              >
                <div className="min-w-0 flex-1">
                  <div className="break-words text-sm font-semibold text-codialis-navy">
                    {echeance.origine === 'ROBOT' && (
                      <Bot
                        className="mr-1.5 inline h-3.5 w-3.5 align-[-2px] text-codialis-gray"
                        aria-label="Tenue par le robot"
                      />
                    )}
                    {echeance.referenceLisible ? `${echeance.referenceLisible} · ` : ''}
                    {echeance.intitule}
                  </div>
                  <div className="text-[11px] text-codialis-gray">{sousTitreEcheance(echeance)}</div>
                </div>
                <Pastille texte={echeance.etatLibelle} ton={echeance.etatTon} />
              </button>
            ))}
          </Carte>
        </div>

        <div className="min-w-0">
          <Carte
            titre="Détections récentes du robot"
            actions={
              <>
                {aAnalyser > 0 && (
                  <div className="flex rounded-lg bg-gray-100 p-0.5 text-xs font-semibold" role="group" aria-label="Filtre des détections">
                    <button
                      type="button"
                      onClick={() => setToutesDetections(false)}
                      aria-pressed={!toutesDetections}
                      className={`min-h-[32px] rounded-md px-2.5 [@media(pointer:coarse)]:min-h-[40px] ${
                        !toutesDetections ? 'bg-white text-codialis-navy shadow-sm' : 'text-codialis-gray'
                      }`}
                    >
                      À analyser ({aAnalyser})
                    </button>
                    <button
                      type="button"
                      onClick={() => setToutesDetections(true)}
                      aria-pressed={toutesDetections}
                      className={`min-h-[32px] rounded-md px-2.5 [@media(pointer:coarse)]:min-h-[40px] ${
                        toutesDetections ? 'bg-white text-codialis-navy shadow-sm' : 'text-codialis-gray'
                      }`}
                    >
                      Toutes
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setSaisieDetection(true)}
                  className="inline-flex min-h-[36px] items-center gap-1 rounded-lg px-2 text-xs font-bold text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[44px]"
                >
                  <PenLine className="h-4 w-4" aria-hidden="true" />
                  Saisir une évolution
                </button>
              </>
            }
          >
            {isLoading && <Chargement />}
            {!isLoading && data && detections.length === 0 && (
              <p className="px-4 py-8 text-sm text-codialis-gray sm:px-6">
                {dernierPassage
                  ? 'Aucune évolution relevée : les fiches et la déclaration d’activité suivies sont inchangées.'
                  : 'Aucune évolution relevée pour le moment.'}
              </p>
            )}
            {detectionsVisibles.map((detection) => (
              <div key={detection.id} className="px-4 py-4 sm:px-6">
                <div className="flex items-start gap-3">
                  <span
                    className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${PASTILLE_NIVEAU[detection.ton]}`}
                    aria-hidden="true"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0 break-words text-sm font-semibold text-codialis-navy">
                        {detection.titre}
                      </div>
                      {detection.statut === 'ANALYSEE' ? (
                        <Pastille
                          texte={detection.impact === 'IMPACT' ? 'Impact' : 'Sans impact'}
                          ton={detection.impact === 'IMPACT' ? 'warning' : 'success'}
                        />
                      ) : (
                        <Pastille texte={detection.niveauLibelle} ton={detection.ton} />
                      )}
                    </div>
                    <div className="mb-1 text-[11px] text-codialis-gray">
                      {detection.sourceLibelle} · {instantLisible(detection.detecteeLe)}
                    </div>
                    <p className="break-words text-xs text-codialis-gray">{detection.detail}</p>
                    {detection.statut === 'ANALYSEE' && detection.analyse && (
                      <p className="mt-1 break-words rounded-lg bg-codialis-sand px-2 py-1.5 text-xs text-codialis-navy">
                        {detection.analyse}
                        {detection.analyseePar && (
                          <span className="text-codialis-gray"> — {detection.analyseePar}</span>
                        )}
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={() => setDetectionOuverte(detection)}
                      className="mt-1 inline-flex min-h-[32px] items-center text-xs font-bold text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[40px]"
                    >
                      {detection.statut === 'ANALYSEE' ? 'Revoir l’analyse →' : 'Analyser l’impact →'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {detections.length > DETECTIONS_REPLIEES && (
              <div className="px-4 py-3 sm:px-6">
                <button
                  type="button"
                  onClick={() => setToutAfficher((avant) => !avant)}
                  className="min-h-[36px] text-xs font-bold text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[44px]"
                >
                  {toutAfficher ? 'Replier' : `Tout afficher (${detections.length})`}
                </button>
              </div>
            )}
          </Carte>
        </div>
      </div>

      <ModaleEcheance
        ouverte={nouvelleEcheance || echeanceOuverte !== null}
        onFermer={() => {
          setNouvelleEcheance(false);
          setEcheanceOuverte(null);
        }}
        echeance={echeanceOuverte}
        sites={data?.sitesSaisissables ?? []}
        preavisMois={preavis}
      />
      <ModaleDetection detection={detectionOuverte} onFermer={() => setDetectionOuverte(null)} />
      <ModaleSaisieDetection ouverte={saisieDetection} onFermer={() => setSaisieDetection(false)} />
      <ModaleSources
        ouverte={sourcesOuvertes}
        onFermer={() => setSourcesOuvertes(false)}
        sources={data?.sources ?? []}
      />
    </div>
  );
}
